import {
  parseISO,
  format,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameYear,
  getWeek,
} from 'date-fns';
import { enUS } from 'date-fns/locale';

const getDateRange = (selectedDate) => {
  if (!selectedDate) return { startDate: null, endDate: null };

  const date = new Date(selectedDate);
  const startDate = date.toLocaleDateString('sv-SE');

  const endDate = new Date(date);
  endDate.setDate(endDate.getDate() + 1);
  const endDateFormatted = endDate.toLocaleDateString('sv-SE');

  return { startDate, endDate: endDateFormatted };
};

export default getDateRange;

export const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

export const isDateInSameWeek = (date, referenceDate) => {
  const startOfWeek = getStartOfWeek(referenceDate);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  return date >= startOfWeek && date <= endOfWeek;
};

/**
 * Format a JS Date to YYYY-MM-DD
 * @param {Date} date
 * @returns {string}
 */
export const toISODate = (date) => {
  if (!(date instanceof Date)) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Given a range [startDate, endDate], return the midpoint as YYYY-MM-DD
 * @param {[Date, Date]} range
 * @returns {string|null}
 */
export const toMidpointISO = (range) => {
  if (!Array.isArray(range) || range.length < 2) return null;
  const [start, end] = range;
  if (!(start instanceof Date) || !(end instanceof Date)) return null;

  const midTime = (start.getTime() + end.getTime()) / 2;
  return toISODate(new Date(midTime));
};

export const getLast7Days = () => {
  const today = new Date();
  const days = [];

  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);

    const label = date
      .toLocaleDateString('en-US', { weekday: 'short', day: '2-digit' })
      .toUpperCase(); // ej: "FRI 03"

    const value = date.toISOString().split('T')[0]; // formato "yyyy-mm-dd"

    days.push({ label, value });
  }

  return days;
};

/**
 * Generates an array of the last 4 full weeks with labels like:
 * [{ table: 'WEEK 26', subtitle: '(JUN 23 – 29)' }, ...]
 *
 * @param {Date} [currentDate=new Date()] - The reference date
 * @returns {Array<{table: string, subtitle: string}>}
 */
export const getLastFourWeeks = (currentDate = new Date()) => {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const WEEKS_TO_SHOW = 4;

  const getMonday = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0); // normaliza
    const day = d.getDay(); // 0..6 (Sun..Sat)
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)); // lunes de esa semana (local)
  };

  const formatDate = (date) =>
    date.toLocaleString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();

  const formatWeek = (weekNumber, start, end) => ({
    table: `WEEK ${weekNumber}`,
    subtitle: `(${formatDate(start)} – ${formatDate(end)})`,
    value: toISODate(start), // <-- primer día de la semana (YYYY-MM-DD)
  });

  const results = [];
  const monday = getMonday(currentDate);

  const getWeekNumber = (date) => {
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const days = Math.floor((date - startOfYear) / MS_PER_DAY);
    return Math.ceil((days + startOfYear.getDay() + 1) / 7);
  };

  for (let i = 0; i < WEEKS_TO_SHOW; i++) {
    const start = new Date(monday.getTime() - i * 7 * MS_PER_DAY);
    const end = new Date(start.getTime() + 6 * MS_PER_DAY);
    const weekNumber = getWeekNumber(start);
    results.push(formatWeek(weekNumber, start, end));
  }

  return results;
};

export const toLongEN = (iso) => format(parseISO(iso), 'EEEE MMMM d, yyyy', { locale: enUS });

// Semana domingo→sábado. Cambia a { weekStartsOn: 1 } si querés lunes.
const WEEK_OPTS = { weekStartsOn: 0, firstWeekContainsDate: 1 };

function formatWeekRange(d) {
  const ws = startOfWeek(d, WEEK_OPTS);
  const we = endOfWeek(d, WEEK_OPTS);

  if (isSameYear(ws, we)) {
    if (isSameMonth(ws, we)) {
      // "Jun 23–29, 2025"
      return `${format(ws, 'MMM d', { locale: enUS })}–${format(we, 'd, yyyy', { locale: enUS })}`;
    }
    // "Dec 30 – Jan 5, 2025"
    return `${format(ws, 'MMM d', { locale: enUS })} – ${format(we, 'MMM d, yyyy', {
      locale: enUS,
    })}`;
  }

  // "Dec 30, 2025 – Jan 5, 2026"
  return `${format(ws, 'MMM d, yyyy', { locale: enUS })} – ${format(we, 'MMM d, yyyy', {
    locale: enUS,
  })}`;
}

/**
 * mode: 'day' | 'week' | 'month'
 * dateISO: 'YYYY-MM-DD'
 */
export function formatByMode(mode, dateISO) {
  const d = parseISO(String(dateISO));

  if (mode === 'month') {
    // "June 2025"
    return format(d, 'MMMM yyyy', { locale: enUS });
  }

  if (mode === 'week') {
    // "Week 26 (Jun 23–29, 2025)"
    const wk = getWeek(d, WEEK_OPTS);
    return `Week ${wk} (${formatWeekRange(d)})`;
  }

  // day → "Sunday June 20, 2025"
  return format(d, 'EEEE MMMM d, yyyy', { locale: enUS });
}

export const addDaysUTC = (yyyyMmDd, days = 1) => {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
};

// --- utils/dates.js ---

/** YYYY-MM-DD en UTC */
function toISODateUTC(dt) {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Devuelve el rango de semana (start/end) que contiene la fecha dada.
 * - dateOrISO: Date o 'YYYY-MM-DD'
 * - weekStartsOn: 1 = lunes (default), 0 = domingo, etc.
 *
 * Retorna:
 * {
 *   start: Date (UTC midnight),
 *   end:   Date (UTC midnight),
 *   startISO: 'YYYY-MM-DD',
 *   endISO:   'YYYY-MM-DD'
 * }
 */
export function getWeekRangeISO(dateOrISO, weekStartsOn = 1) {
  // Normalizamos entrada a una fecha UTC “00:00”
  let d;
  if (dateOrISO instanceof Date) {
    d = new Date(Date.UTC(dateOrISO.getFullYear(), dateOrISO.getMonth(), dateOrISO.getDate()));
  } else {
    // asumimos 'YYYY-MM-DD'
    const [y, m, day] = String(dateOrISO).split('-').map(Number);
    d = new Date(Date.UTC(y, m - 1, day));
  }

  // Día de la semana en UTC (0=Dom, 1=Lun, ... 6=Sab)
  const utcDow = d.getUTCDay();

  // Distancia desde el inicio de semana deseado
  const diffFromStart = (utcDow - weekStartsOn + 7) % 7;

  // Lunes (o el día elegido) de esa semana en UTC
  const start = new Date(d);
  start.setUTCDate(d.getUTCDate() - diffFromStart);

  // Fin de semana (6 días después)
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);

  return {
    start,
    end,
    startISO: toISODateUTC(start),
    endISO: toISODateUTC(end),
  };
}
